"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import type { User, LoginRequest, LoginResponse, RefreshTokenResponse, UserRole } from "@/types/auth";
import type { NavigationItem, PermissionModuleCode, PermissionSnapshot, PermissionsBootstrapResponse } from "@/types/permissions";
import { hasRequiredRole } from "@/lib/authorization";

export interface ImpersonatedUserInfo {
  id: number;
  name: string;
  login: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  permissions: PermissionSnapshot | null;
  menu: NavigationItem[];
  isLoading: boolean;
  isImpersonating: boolean;
  impersonatedRole: UserRole | null;
  impersonatedUser: ImpersonatedUserInfo | null;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  impersonateRole: (role: UserRole, targetUser?: ImpersonatedUserInfo) => void;
  exitImpersonation: () => void;
  hasModuleAccess: (moduleCode: PermissionModuleCode) => boolean;
  canViewMonetaryValues: (moduleCode: PermissionModuleCode) => boolean;
  /** Verifica a role efetiva (considerando impersonation) contra uma lista de roles aceitas. `super` sempre passa. */
  hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = "superbar_access_token";
const REFRESH_TOKEN_KEY = "superbar_refresh_token";
const USER_KEY = "superbar_user";
const PERMISSIONS_KEY = "superbar_permissions";
const IMPERSONATION_KEY = "superbar_impersonation";
const IMPERSONATION_USER_KEY = "superbar_impersonation_user";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<PermissionSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatedRole, setImpersonatedRole] = useState<UserRole | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUserInfo | null>(null);

  const clearStoredAuth = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(IMPERSONATION_USER_KEY);
  }, []);

  const fetchSessionBootstrap = useCallback(async (token: string, nextImpersonatedRole?: UserRole | null) => {
    const headers = new Headers({ Authorization: `Bearer ${token}` });

    if (nextImpersonatedRole) {
      headers.set("x-superbar-impersonate-role", nextImpersonatedRole);
    }

    const response = await fetch("/api/auth/me", {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json() as Promise<PermissionsBootstrapResponse>;
  }, []);

  const applySessionBootstrap = useCallback((nextUser: User, nextPermissions: PermissionSnapshot, token?: string | null) => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setAccessToken(token);
    }

    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(nextPermissions));

    setUser(nextUser);
    setPermissions(nextPermissions);
  }, []);

  const hydrateSession = useCallback(async (token: string, nextImpersonatedRole?: UserRole | null) => {
    const bootstrap = await fetchSessionBootstrap(token, nextImpersonatedRole);
    applySessionBootstrap(bootstrap.user, bootstrap.permissions, token);
    return bootstrap;
  }, [applySessionBootstrap, fetchSessionBootstrap]);

  const loadStoredAuth = useCallback(() => {
    const load = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);
        const storedPermissions = localStorage.getItem(PERMISSIONS_KEY);
        const storedImpersonation = localStorage.getItem(IMPERSONATION_KEY);
        const storedImpersonatedUser = localStorage.getItem(IMPERSONATION_USER_KEY);

        if (storedToken && storedUser) {
          setAccessToken(storedToken);
          setUser(JSON.parse(storedUser));
          if (storedPermissions) {
            setPermissions(JSON.parse(storedPermissions));
          }
          if (storedImpersonation) {
            setImpersonatedRole(storedImpersonation as UserRole);
          }
          if (storedImpersonatedUser) {
            setImpersonatedUser(JSON.parse(storedImpersonatedUser));
          }

          await hydrateSession(storedToken, storedImpersonation as UserRole | null);
        }
      } catch (error) {
        console.error("Error loading stored auth:", error);
        clearStoredAuth();
        setAccessToken(null);
        setUser(null);
        setPermissions(null);
        setImpersonatedRole(null);
        setImpersonatedUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [clearStoredAuth, hydrateSession]);

  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  const login = useCallback(async (credentials: LoginRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data: LoginResponse = await response.json();

      if (!data.success || !data.user || !data.permissions || !data.accessToken || !data.refreshToken) {
        return { success: false, error: data.error || "Login failed" };
      }

      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      applySessionBootstrap(data.user, data.permissions, data.accessToken);
      setImpersonatedRole(null);
      setImpersonatedUser(null);
      localStorage.removeItem(IMPERSONATION_KEY);
      localStorage.removeItem(IMPERSONATION_USER_KEY);

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Network error" };
    }
  }, [applySessionBootstrap]);

  const logout = useCallback(async () => {
    try {
      if (accessToken) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearStoredAuth();
      setAccessToken(null);
      setUser(null);
      setPermissions(null);
      setImpersonatedRole(null);
      setImpersonatedUser(null);
    }
  }, [accessToken, clearStoredAuth]);

  const impersonateRole = (role: UserRole, targetUser?: ImpersonatedUserInfo) => {
    if (user?.role !== 'super') return;
    localStorage.setItem(IMPERSONATION_KEY, role);
    setImpersonatedRole(role);
    if (targetUser) {
      localStorage.setItem(IMPERSONATION_USER_KEY, JSON.stringify(targetUser));
      setImpersonatedUser(targetUser);
    } else {
      localStorage.removeItem(IMPERSONATION_USER_KEY);
      setImpersonatedUser(null);
    }
    if (accessToken) {
      void hydrateSession(accessToken, role);
    }
  };

  const exitImpersonation = () => {
    localStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(IMPERSONATION_USER_KEY);
    setImpersonatedRole(null);
    setImpersonatedUser(null);
    if (accessToken) {
      void hydrateSession(accessToken, null);
    }
  };

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return false;
      }

      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      const data: RefreshTokenResponse = await response.json();

      if (!data.success || !data.accessToken || !data.user || !data.permissions) {
        await logout();
        return false;
      }

      if (impersonatedRole) {
        await hydrateSession(data.accessToken, impersonatedRole);
      } else {
        applySessionBootstrap(data.user, data.permissions, data.accessToken);
      }

      return true;
    } catch (error) {
      console.error("Refresh token error:", error);
      await logout();
      return false;
    }
  }, [applySessionBootstrap, hydrateSession, impersonatedRole, logout]);

  useEffect(() => {
    if (!accessToken) return;

    const interval = setInterval(async () => {
      await refreshAccessToken();
    }, 2 * 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [accessToken, refreshAccessToken]);

  const effectiveUser = useMemo(
    () => (user && impersonatedRole ? { ...user, role: impersonatedRole, roles: [impersonatedRole] } : user),
    [user, impersonatedRole],
  );

  const accessibleModuleCodes = permissions?.accessibleModuleCodes;
  const permissionModules = permissions?.modules;

  const hasModuleAccess = useCallback((moduleCode: PermissionModuleCode) => {
    return accessibleModuleCodes?.includes(moduleCode) ?? false;
  }, [accessibleModuleCodes]);

  const canViewMonetaryValues = useCallback((moduleCode: PermissionModuleCode) => {
    return permissionModules?.some((module) => module.code === moduleCode && module.enabled && module.canViewValues) ?? false;
  }, [permissionModules]);

  const hasRole = useCallback((roles: UserRole[]) => {
    return effectiveUser ? hasRequiredRole(effectiveUser.roles, undefined, roles) : false;
  }, [effectiveUser]);

  const value: AuthContextType = {
    user: effectiveUser,
    accessToken,
    permissions,
    menu: permissions?.menu ?? [],
    isLoading,
    isImpersonating: !!impersonatedRole,
    impersonatedRole,
    impersonatedUser,
    login,
    logout,
    refreshAccessToken,
    impersonateRole,
    exitImpersonation,
    hasModuleAccess,
    canViewMonetaryValues,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
